﻿/*!
// <copyright file="AppletMessaging.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/* jshint -W040 */
"use strict";

declare global {
    interface Window {
        dynamicsOfficeBuild: any;
    }
}

/* jshint +W040 */

import * as $ from "jquery";
import { util, $format } from "./util";

/** Contains functionality and contracts used to facilitate messaging between the app and applets. */
export namespace appletMessaging {

    /**
     * The details that enable messaging from applets to the app. These details are required to be setup by each applet to properly
     * facilitate messaging from an applet to the app.
     */
    export namespace MessagingDetails {

        /** The ID of the consuming applet. */
        export let AppletID: string;

        /** The message handler used by the consuming applet. */
        export let MessageHandler: MessageHandler;

        /** The messaging details surrounding the app window. */
        export namespace AppWindow {

            /** The app's source window to send messages to. */
            export let Source: Window;

            /** The app's origin to send messages to. */
            export let Origin: string;

            export interface SendMessageOptions {

                /**
                 * Indicates the amount of time in milliseconds to wait for a response before the request is considered timed out and a failed response is sent
                 * in its place; this only applied to request messages that have an expected response type set. To wait indefinitely set the timeout to 0.
                 */
                ResponseTimeout?: number;
            }

            /**
             * Sends the provided message to the app.
             * @param message The message to send to the app.
             * @param options The options that determine how the message should be sent.
             */
            export function sendMessage(message: any, options?: SendMessageOptions): void {
                appletMessaging.MessagingDetails.MessageHandler.sendMessage(
                    appletMessaging.MessagingDetails.AppWindow.Source,
                    appletMessaging.MessagingDetails.AppWindow.Origin,
                    message, options);
            }
        }
    }

    /* jshint -W069 */
    /** The known statuses for an applet response. */
    export enum ResponseStatus {

        /** The status for a successful response. */
        success = 0,

        /** The status for a failed response. */
        failure = 1
    }
    /* jshint +W069 */

    /** The well known resources available to all applets. */
    export namespace WellKnownResources {

        /** The ID of the data source. */
        export const Data = "Microsoft.Dynamics.Platform.Integration.Office.DataResource"
    }

    /** The message types related to applet messaging. */
    export namespace MessageTypes {

        /** The message type of the applet ready message. */
        export const AppletReady = "AppletReady";

        /** The message type of the applet configuration message. */
        export const AppletConfiguration = "AppletConfiguration";

        /** The message type of the applet action message. */
        export const AppletAction = "AppletAction";

        /** The message type of the options changed message. */
        export const OptionsChanged = "OptionsChanged";

        /** The message type of the update applet options message. */
        export const UpdateAppletOptions = "UpdateAppletOptions";

        /** The message type of the settings changed message. */
        export const SaveSettings = "SaveSettings";

        export const SetSessionStorageItem = "SetSessionStorageItem";

        /** The message type of the document event subscription message. */
        export const DocumentEventSubscription = "DocumentEventSubscription";

        /** The message type of the binding event subscription message. */
        export const BindingEventSubscription = "BindingEventSubscription";

        /** The message type of the office event message. */
        export const OfficeEvent = "OfficeEvent";

        /** The message type of the binding method message. */
        export const BindingMethod = "BindingMethod";

        /** The message type of the document method message. */
        export const DocumentMethod = "DocumentMethod";

        /** The message type of the bindings method message. */
        export const BindingsMethod = "BindingsMethod";

        /** The message type of the Office action message. */
        export const OfficeAction = "OfficeAction";

        /** The message type of the add notification message. */
        export const AddNotification = "AddNotification";

        /** The message type of the prompt user message. */
        export const PromptUser = "PromptUser";

        /** The message type of the log event message. */
        export const LogEvent = "LogEvent";

        /** The message type of the begin operation message. */
        export const BeginOperation = "BeginOperation";

        /** The message type of the update operation message. */
        export const UpdateOperation = "UpdateOperation";

        /** The message type of the end operation message. */
        export const EndOperation = "EndOperation";

        /** The message type of the set status message. */
        export const SetStatus = "SetStatus";

        /** The message type of the resource message. */
        export const Resource = "Resource";

        /** The message type of the inter-applet message. */
        export const InterApplet = "InterApplet";

        /** The message type of the dependent applet unloaded message. */
        export const DependentAppletUnloaded = "DependentAppletUnloaded";

        /** The message type of the open options message. */
        export const OpenOptions = "OpenOptions";

        /** The message type of the close options message. */
        export const CloseOptions = "CloseOptions";

        /** The message type of the close options message. */
        export const ChangeConnectionAddress = "ChangeConnectionAddress";

        /** The message type of the focus in iframe message. */
        export const FocusInIFame = "FocusInIFame";
    }

    /**
     * The map of expected response types, keyed by message type. Upon request creation, the constructor uses
     * this map to determine if there is a corresponding response.
     */
    export const expectedResponseTypes: util.PropertyBag<Function> = {};

    export interface ILinkOptions {
        Text?: string;
        Action?: string | (() => void);
        AppAction?: AppActions;
    }

    export interface RequestOptions {

        /** The ID of the applet subscribing to the document event(s); defaults to global messaging details applet ID. */
        AppletID?: string;

        /** Indicates if the request's data should be included in the response; defaults to true. */
        IncludeDataInResponse?: boolean;
    }

    /** The requests related to applet messaging. */
    export namespace Requests {

        /** Represents the base applet messaging request, which contains the common properties needed for a given request. */
        export class Request<TData> {

            /** The type of message. */
            public Type: string;

            /** The ID of the applet the message pertains to. */
            public AppletID: string;

            /** The detailed data for the request. */
            public Data: TData;

            /** Indicates if the request's data should be included in the response. */
            public IncludeDataInResponse: boolean;

            /** The message ID that uniquely identifies the message. */
            public MessageID: string;

            /** The type of the response that is expected; the constructor should take the request as the first parameter. If omitted, the request is considered a one-way message. */
            public ExpectedResponseType: Function;

            /**
             * @param messageType The type of message.
             * @param options The options to include in the request.
             */
            constructor(messageType: string, options: RequestOptions = {}) {

                // If the include data in response flag isn't set, default it to true
                if (options.IncludeDataInResponse === undefined) {
                    options.IncludeDataInResponse = true;
                }

                // If the applet ID isn't set, default it to the global messaging details applet ID
                if (options.AppletID === undefined) {
                    options.AppletID = appletMessaging.MessagingDetails.AppletID;
                }

                this.Type = messageType;
                this.AppletID = options.AppletID;
                this.Data = undefined;
                this.IncludeDataInResponse = options.IncludeDataInResponse;
                this.MessageID = appletMessaging.createUniqueMessageID(options.AppletID, messageType);
                this.ExpectedResponseType = appletMessaging.expectedResponseTypes[messageType];
            }
        }

        /** Represents the request used to indicate that an applet is ready to be configured, where the request is sent from an applet to the app prior to applet configuration. */
        export class AppletReadyRequest extends Request<void> {

            /**
             * @param appletID The ID of the applet to configure.
             */
            constructor(appletID: string) {
                super(MessageTypes.AppletReady, { AppletID: appletID });
            }
        }

        export interface AppletConfigurationRequestData {

            /** The version of the app API available. */
            AppVersion: number;

            /** The Office application hosting the app. */
            ApplicationType: util.ApplicationType;

            /** The settings for the applet. */
            Settings: any;

            /** The current app language. */
            Language: string;

            /** The configuration retrieved from the server. */
            ServerConfig: any;

            /** The add-in's property bag of features. */
            Features?: util.AddinFeatures;

            /** The applet's isolated session storage. */
            SessionStorage: util.PropertyBag<any>;
        }

        export interface AppletConfigurationRequestOptions {

            /** The version of the app API available. */
            AppVersion?: number;

            /** The Office application hosting the app. */
            ApplicationType?: util.ApplicationType;

            /** The settings for the applet. */
            Settings?: any;

            /** A string representing the current app language. */
            Language?: string;

            /** The configuration retrieved from the server. */
            ServerConfig?: any;

            /** The add-in's property bag of features. */
            Features?: util.AddinFeatures;

            /** The applet's isolated session storage. */
            SessionStorage?: util.PropertyBag<any>;
        }

        /** Represents the request used to configure an applet, where the request is sent from the app to the applet to configure. */
        export class AppletConfigurationRequest extends Request<AppletConfigurationRequestData> {

            /**
             * @param appletID The ID of the applet to configure.
             * @param options The options to include in the request.
             */
            constructor(appletID: string, options: AppletConfigurationRequestOptions = {}) {

                super(
                    appletMessaging.MessageTypes.AppletConfiguration,
                    {
                        AppletID: appletID
                    });

                this.Data = {
                    AppVersion: options.AppVersion,
                    ApplicationType: options.ApplicationType,
                    Settings: options.Settings,
                    Language: options.Language,
                    ServerConfig: options.ServerConfig,
                    SessionStorage: options.SessionStorage,
                    Features: options.Features
                }
            }
        }

        export interface AppletActionRequestData {

            /** The name of the action requested for the applet to perform. */
            ActionName: string;
        }

        /**
         * Represents the request used to perform an action in an applet, where the action name is the name of an action the applet knows
         * how to handle; if not the applet will presumably do nothing.  The request is sent from the app to an applet. No response
         * is sent for this request.
         */
        export class AppletActionRequest extends Request<AppletActionRequestData> {

            /**
             * @param appletID The ID of the applet requested to perform the action.
             * @param actionName The name of the action requested for the applet to perform.
             */
            constructor(appletID, actionName) {
                super(appletMessaging.MessageTypes.AppletAction, { AppletID: appletID });
                this.Data = { ActionName: actionName };
            }
        }

        export interface AppletOptionsChangedRequestData {

            /** The current applet specific options. */
            OptionsGroups: OptionsGroup[];
        }

        /**
         * Represents the request used to notify an applet that its options have changed.
         * The request is sent from the app to an applet.
         */
        export class AppletOptionsChangedRequest extends Request<AppletOptionsChangedRequestData> {

            /**
             * @param appletID The ID of the applet whose options have changed.
             * @param optionsGroups The current applet specific options.
             */
            constructor(appletID: string, optionsGroups: OptionsGroup[]) {

                super(
                    appletMessaging.MessageTypes.OptionsChanged,
                    {
                        AppletID: appletID
                    });

                this.Data = { OptionsGroups: optionsGroups };
            }
        }

        export interface UpdateAppletOptionsRequestData {

            /** The applet's options to update. */
            UpdatedOptions: UpdatedOption[];
        }

        /** Represents the request used to update an applet's options. The request is sent from an applet to the app. */
        export class UpdateAppletOptionsRequest extends Request<UpdateAppletOptionsRequestData> {

            constructor() {
                super(appletMessaging.MessageTypes.UpdateAppletOptions);
                this.Data = { UpdatedOptions: [] };
            }
        }

        export interface SaveSettingsRequestData {

            /** The applet's settings to save. */
            Settings: any;
        }

        /** Represents the request used to save an applet's settings. The request is sent from an applet to the app. */
        export class SaveSettingsRequest extends Request<SaveSettingsRequestData> {

            /**
             * @param settings The applet's settings to save.
             * @param options The options to include in the request.
             */
            constructor(settings: any, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.SaveSettings, options);
                this.Data = { Settings: settings };
            }
        }

        export interface SetSessionStorageItemRequestData {

            /** The key of the item to store. */
            Key: string;

            /** The value of the item to store. */
            Value: string;
        }

        /** Represents the request used to set an item into an applet's isolated session storage. */
        export class SetSessionStorageItemRequest extends Request<SetSessionStorageItemRequestData> {

            /**
             * @param key The key of the item to store.
             * @param value The value of the item to store.
             * @param options The options to include in the request.
             */
            constructor(key: string, value: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.SetSessionStorageItem, options);
                this.Data = { Key: key, Value: value };
            }
        }

        export interface DocumentEventSubscriptionRequestData {

            /** The document events to subscribe to. */
            DocumentEventSubscriptions: string[];
        }

        /** Represents the request used to subscribe to document events, where the request is sent from an applet to the app. */
        export class DocumentEventSubscriptionRequest extends Request<DocumentEventSubscriptionRequestData> {

            /**
             * @param options The options to include in the request.
             */
            constructor(options?: RequestOptions) {
                super(appletMessaging.MessageTypes.DocumentEventSubscription, options);
                this.Data = { DocumentEventSubscriptions: [] };
            }
        }

        export interface BindingEventSubscriptionRequestData {

            /** The ID of the binding containing the events to subscribe to. */
            BindingID: string;

            /** The binding events to subscribe to. */
            BindingEventSubscriptions: string[];
        }

        /** Represents the request used to subscribe to binding events, where the request is sent from an applet to the app. */
        export class BindingEventSubscriptionRequest extends Request<BindingEventSubscriptionRequestData> {

            /**
             * @param bindingID The ID of the binding from which to subscribe events.
             * @param options The options to include in the request.
             */
            constructor(bindingID: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.BindingEventSubscription, options);
                this.Data = { BindingID: bindingID, BindingEventSubscriptions: [] };
            }
        }

        export interface OfficeEventRequestData {

            /** Indicates where the event originated (e.g. document or binding). */
            EventSource: OfficeEventSource;

            /** The event args received from the encountered Office event. */
            EventArgs: any;
        }

        /** Represents the request sent when a subscribed Office event is encountered in the app, where the request is sent from the app to the subscribed applets. */
        export class OfficeEventRequest extends Request<OfficeEventRequestData> {

            /**
             * @param appletID The ID of the applet that was subscribed to the encountered Office event.
             * @param eventSource Indicates where the event originated (e.g. document or binding).
             * @param eventArgs The event args received from the encountered Office event.
             */
            constructor(appletID: string, eventSource: OfficeEventSource, eventArgs: any) {
                super(appletMessaging.MessageTypes.OfficeEvent, { AppletID: appletID });
                this.Data = { EventSource: eventSource, EventArgs: eventArgs };
            }
        }

        export interface BindingMethodRequestData {

            /** The ID of the binding upon which to call the method(s). */
            BindingID: string;

            /** The information for the method(s) to call upon the binding. */
            Methods: OfficeMethodInformation[];
        }

        /** Represents the request used to execute methods for the Apps for Office API on a binding, where the request is sent from an applet to the app. */
        export class BindingMethodRequest extends Request<BindingMethodRequestData> {

            /**
             * @param bindingID The ID of the binding upon which to call the method(s).
             * @param options The options to include in the request.
             */
            constructor(bindingID: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.BindingMethod, options);
                this.Data = { BindingID: bindingID, Methods: [] };
            }
        }

        export interface DocumentMethodRequestData {

            /** The information for the method to call upon the document. */
            Method: OfficeMethodInformation;
        }

        /** Represents the request used to execute a method for the Apps for Office API on the document, where the request is sent from an applet to the app. */
        export class DocumentMethodRequest extends Request<DocumentMethodRequestData> {

            /**
             * @param method The information for the method to call upon the document.
             * @param options The options to include in the request.
             */
            constructor(method: OfficeMethodInformation, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.DocumentMethod, options);
                this.Data = { Method: method };
            }
        }

        export interface BindingsMethodRequestData {

            /** The information for the method to call upon the document bindings. */
            Method: OfficeMethodInformation;
        }

        /**
         * Represents the request used to execute a method for the Apps for Office API on the document bindings, where the request is sent from an applet
         * to the app.
         */
        export class BindingsMethodRequest extends Request<BindingsMethodRequestData> {

            /**
             * @param method The information for the method to call upon the document bindings.
             * @param options The options to include in the request.
             */
            constructor(method: OfficeMethodInformation, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.BindingsMethod, options);
                this.Data = { Method: method };
            }
        }

        export interface OfficeActionRequestData {

            /** The actions to execute using the Office API. */
            Actions: OfficeAction[];
        }

        /** Represents the request used to execute a series of actions using the Office API, where the request is sent from an applet to the app. */
        export class OfficeActionRequest extends Request<OfficeActionRequestData> {

            /**
             * @param actions The actions to execute.
             * @param options The options to include in the request.
             */
            constructor(actions: OfficeAction[], options?: RequestOptions) {
                super(appletMessaging.MessageTypes.OfficeAction, options);
                this.Data = { Actions: actions };
            }
        }

        export interface AddNotificationRequestData {

            /** The notification to add. */
            Notification: Notification;
        }

        /**
         * Represents the request used to add a notification in the app, where the request is sent from an applet to the app. No response is sent
         * for this request.
         */
        export class AddNotificationRequest extends Request<AddNotificationRequestData> {

            /**
             * @param notification The notification to add.
             * @param options The options to include in the request.
             */
            constructor(notification: Notification, options?: RequestOptions) {

                super(appletMessaging.MessageTypes.AddNotification, options);

                if (notification.TelemetryProperties === undefined) {
                    notification.TelemetryProperties = {};
                }

                appletMessaging.addBuildInfoToMessage(notification.TelemetryProperties);

                this.Data = { Notification: notification };
            }
        }

        export interface PromptUserRequestData {

            /** The message, in the form of a question, to prompt the user with. */
            Message: string;

            /** The details to display to the user. */
            Details: string;
        }

        export interface PromptUserRequestOptions extends RequestOptions {

            /** The details to display to the user. */
            Details?: string;
        }

        /** Represents the request used to prompt the user for a boolean value, where the request is sent from an applet to the app. */
        export class PromptUserRequest extends Request<PromptUserRequestData> {

            /**
             * @param message The message, in the form of a question, to prompt the user with.
             * @param options The options to include in the request.
             */
            constructor(message: string, options: PromptUserRequestOptions = {}) {
                super(appletMessaging.MessageTypes.PromptUser, options);
                this.Data = { Message: message, Details: options.Details };
            }
        }

        export interface LogEventRequestData {

            /** The event to add. */
            Event: string;

            /** The message type. */
            MessageType: util.MessageType;

            /** The telemetry level. */
            TelemetryType: util.telemetry.Type;

            /** Additional detailed telemetry. */
            TelemetryDetails: string | util.PropertyBag<any>;
        }

        export interface LogEventRequestOptions extends RequestOptions {

            /** The type of message. */
            MessageType?: util.MessageType;

            /** The severity level for telemetry. */
            TelemetryType?: util.telemetry.Type;

            /** Event details for telemetry. */
            TelemetryDetails?: string | util.PropertyBag<any>;
        }

        /**
         * Represents the request used to log an event in the app, where the request is sent from an applet to the app. No response is sent
         * for this request.
         */
        export class LogEventRequest extends Request<LogEventRequestData> {

            /**
             * @param event The event to add.
             * @param options The options to include in the request.
             */
            constructor(event: string, options: LogEventRequestOptions = {}) {
                super(appletMessaging.MessageTypes.LogEvent, options);
                appletMessaging.addBuildInfoToMessage(options);
                this.Data = {
                    Event: event,
                    MessageType: options.MessageType,
                    TelemetryType: options.TelemetryType,
                    TelemetryDetails: options.TelemetryDetails
                };
            }
        }

        export interface BeginOperationRequestData {

            /** The ID used to track the operation from beginning to end so the message can be updated and cleared when the operation completes. */
            OperationID: string;

            /** The message to display indicating work is taking place for the specified operation. */
            Message: string;
        }

        /**
         * Represents the request used to notify the app of a blocking operation in the applet, where the request is sent from an applet to the app. No response is sent
         * for this request.
         */
        export class BeginOperationRequest extends Request<BeginOperationRequestData> {

            /**
             * @param operationID The ID used to track the operation from beginning to end so the message can be updated and cleared when the operation completes.
             * @param message The message to display indicating work is taking place for the specified operation.
             * @param options The options to include in the request.
             */
            constructor(operationID: string, message: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.BeginOperation, options);
                this.Data = { OperationID: operationID, Message: message };
            }
        }

        export interface UpdateOperationRequestData {

            /** The ID of the operation to update. */
            OperationID: string;

            /** The message to display indicating work has been updated for the specified operation. */
            Message: string;
        }

        /**
         * Represents the request used to update the operation message displayed by the app, where the request is sent from an applet to the app. No response is sent
         * for this request.
         */
        export class UpdateOperationRequest extends Request<UpdateOperationRequestData> {

            /**
             * @param operationID The ID of the operation to update.
             * @param message The message to display indicating work is taking place for the specified operation.
             * @param options The options to include in the request.
             */
            constructor(operationID: string, message: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.UpdateOperation, options);
                this.Data = { OperationID: operationID, Message: message };
            }
        }

        export interface EndOperationRequestData {

            /** The ID of the completed operation. */
            OperationID: string;

            /** The message to display indicating work has completed for the specified operation. */
            Message: string;

            /** Indicates whether a time stamp should be displayed with the notification. */
            IncludeTimeStamp: boolean;

            /** Detailed telemetry data. */
            TelemetryDetails: string | util.PropertyBag<any>;
        }

        export interface EndOperationRequestOptions extends RequestOptions {

            /** Indicates whether a time stamp should be displayed with the notification; true by default. */
            IncludeTimeStamp?: boolean;

            /** Property bag of detailed telemetry data. */
            TelemetryDetails?: string | util.PropertyBag<any>;
        }

        /**
         * Represents the request used to notify the app that a blocking operation in the applet has completed, where the request is sent from an applet to the app. No response is sent
         * for this request.
         */
        export class EndOperationRequest extends Request<EndOperationRequestData> {

            /**
             * @param operationID The ID of the completed operation.
             * @param message The message to display indicating work has completed for the specified operation.
             * @param options The options to include in the request.
             */
            constructor(operationID: string, message: string, options: EndOperationRequestOptions = {}) {

                // If the include time stamp flag isn't set, default it to true
                if (options.IncludeTimeStamp === undefined) {
                    options.IncludeTimeStamp = true;
                }

                super(appletMessaging.MessageTypes.EndOperation, options);
                appletMessaging.addBuildInfoToMessage(options);

                this.Data = {
                    OperationID: operationID,
                    Message: message,
                    IncludeTimeStamp: options.IncludeTimeStamp,
                    TelemetryDetails: options.TelemetryDetails
                };
            }
        }

        export interface SetStatusRequestData {

            /** The non-operational status message to display. */
            Message: string;
        }

        /** Represents the request used to set a non-operational status message. */
        export class SetStatusRequest extends Request<SetStatusRequestData>{

            /**
             * @param message The non-operational status message to display.
             */
            constructor(message: string) {
                super(appletMessaging.MessageTypes.SetStatus);

                this.Data = {
                    Message: message
                };
            }
        }

        export interface ResourceRequestData {

            /** The ID of the resource to interact with. */
            ResourceID: string;

            /** The request object to send to the resource. */
            ResourceRequest: any;
        }

        export interface ResourceRequestOptions extends RequestOptions {

            /** The request object to send to the resource. */
            ResourceRequest?: any;
        }

        /**
         * Represents the request used to interact with a resource in the app, where the request is sent from an applet to the app. If no resource request is provided, the request will
         * just ensure the resource is loaded.
         */
        export class ResourceRequest extends Request<ResourceRequestData> {

            /**
             * @param resourceID The ID of the resource to interact with.
             * @param options The options to include in the request.
             */
            constructor(resourceID: string, options: ResourceRequestOptions = {}) {
                super(appletMessaging.MessageTypes.Resource, options);
                this.Data = { ResourceID: resourceID, ResourceRequest: options.ResourceRequest };
            }
        }

        export interface InterAppletRequestData {

            /** The ID of the destination applet for the applet request. */
            DestinationAppletID: string;

            /** The request object to send to the applet. */
            AppletRequest: any;
        }

        /**
         * Represents the request used to interact with another applet in the app, where the request is sent from an applet to the app.
         * Applets cannot send messages to other applets without registering as a dependent of the destination applet or first being sent
         * a message from a dependent applet.
         */
        export class InterAppletRequest extends Request<InterAppletRequestData> {

            /**
             * @param destinationAppletID The ID of the destination applet for the applet request.
             * @param appletRequest The request object to send to the applet.
             * @param options The options to include in the request.
             */
            constructor(destinationAppletID: string, appletRequest: any, options: RequestOptions) {
                super(appletMessaging.MessageTypes.InterApplet, options);
                this.Data = { DestinationAppletID: destinationAppletID, AppletRequest: appletRequest };
            }
        }

        export interface DependentAppletUnloadedRequestData {

            /** The ID of the dependent applet that was unloaded. */
            DependentAppletID: string;
        }

        /**
         * Represents the request used to indicate to an applet that a dependent applet has been unloaded. An applet would only be sent this
         * message if a dependent applet had communicated with it, and that dependent applet was since unloaded. This request has no response.
         */
        export class DependentAppletUnloadedRequest extends Request<DependentAppletUnloadedRequestData> {

            /**
             * @param dependentAppletID The ID of the dependent applet that was unloaded.
             * @param options The options to include in the request.
             */
            constructor(dependentAppletID, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.DependentAppletUnloaded, options);
                this.Data = { DependentAppletID: dependentAppletID };
            }
        }

        /** Represents the request sent from the applet to the indicate to the app that the options should be opened. This request has no response. */
        export class OpenOptionsRequest extends Request<void> {

            constructor() {
                super(appletMessaging.MessageTypes.OpenOptions);
            }
        }

        /** Represents the request sent from the applet to the indicate to the app that the options should be closed. This request has no response. */
        export class CloseOptionsRequest extends Request<void> {

            constructor() {
                super(appletMessaging.MessageTypes.CloseOptions);
            }
        }

        export interface ChangeConnectionAddressRequestData {

            /** The connection address to which the add-in should reconnect. */
            ConnectionAddress: string;
        }

        /**
         * Represents the request used to change the app's connection address, where the request is sent from an applet to the app. The user then has to acknowledge that
         * they want to change the connection address, for security reasons. Once they have responded to that prompt, a response will be sent to the applet letting it
         * know the user's response. If the user accepts the change address request, the app will still send the response, but will also begin to reload itself with the new
         * connection address.
         */
        export class ChangeConnectionAddressRequest extends Request<ChangeConnectionAddressRequestData> {

            /**
             * @param connectionAddress The connection address to which the add-in should reconnect.
             * @param options The options to include in the request.
             */
            constructor(connectionAddress: string, options?: RequestOptions) {
                super(appletMessaging.MessageTypes.ChangeConnectionAddress, options);
                this.Data = { ConnectionAddress: connectionAddress };
            }
        }

        export interface GetFocusRequestData { }

        /** Represents the request used to subscribe to focus events, where the request is sent from an app to the applet. */
        export class GetFocusRequest extends Request<GetFocusRequestData> {

            /**
             * @param options The options to include in the request.
             */
            constructor(options?: RequestOptions) {
                super(appletMessaging.MessageTypes.FocusInIFame, options);
                this.Data = { };
            }
        }
    }

    export type RequestType = Requests.Request<any>;

    /** The responses related to applet messaging. */
    export namespace Responses {

        /** Represents the base applet messaging response, which contains the common properties needed for a given response. */
        export class Response<TRequest extends Requests.Request<any>, TData> {

            /** The request message that correlates to the response. */
            public Request: TRequest;

            /** The status of the response, indicating success or failure. */
            public Status: ResponseStatus;

            /** The description of why the message failed; it only applied when the status indicates failure. */
            public FailureMessage: string;

            /** The detailed data for the response. */
            public Data: TData;

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: TRequest) {

                this.Request = request;

                if (!request.IncludeDataInResponse) {
                    this.Request.Data = undefined;
                }

                this.Status = undefined;
                this.FailureMessage = undefined;
                this.Data = undefined;
            }
        }

        export interface AppletConfigurationResponseData {

            /** The current applet specific options. */
            OptionsGroups: OptionsGroup[];

            /** The property bag of failure telemetry. */
            FailureTelemetry: string | util.PropertyBag<any>;
        }

        export interface AppletConfigurationResponseOptions {

            /** The array of appletMessaging.OptionsGroup objects that represent the available applet specific options. */
            OptionsGroups?: OptionsGroup[];
        }

        /**
         * Represents the response for configuring an applet, where the response is sent from the applet back to the app
         * after the applet has been configured.
         */
        export class AppletConfigurationResponse extends Response<Requests.AppletConfigurationRequest, AppletConfigurationResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param options The options to include in the request.
             */
            constructor(request: Requests.AppletConfigurationRequest, options: AppletConfigurationResponseOptions = {}) {
                super(request);
                this.Data = { OptionsGroups: options.OptionsGroups, FailureTelemetry: undefined };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.AppletConfiguration] = AppletConfigurationResponse;

        export interface AppletOptionsChangedResponseData {

            /** The applet settings to save. */
            Settings: any;
        }

        /** Represents the response for notifying an applet that its options have changed, where the response is sent from the applet back to the app. */
        export class AppletOptionsChangedResponse extends Response<Requests.AppletOptionsChangedRequest, AppletOptionsChangedResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param settings The applet settings to save.
             */
            constructor(request: Requests.AppletOptionsChangedRequest, settings: any) {
                super(request);
                this.Data = { Settings: settings };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.OptionsChanged] = AppletOptionsChangedResponse;

        /**
         * Represents the response for updating an applet's options, where the response is sent from the app to the originating applet
         * after the applet has requested options to be updated.
         */
        export class UpdateAppletOptionsResponse extends Response<Requests.UpdateAppletOptionsRequest, void> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.UpdateAppletOptionsRequest) {
                super(request);
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.UpdateAppletOptions] = UpdateAppletOptionsResponse;

        /**
         * Represents the response for saving an applet's settings, where the response is sent from the app to the originating applet
         * after the applet has requested its settings to be saved.
         */
        export class SaveSettingsResponse extends Response<Requests.SaveSettingsRequest, void> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.SaveSettingsRequest) {
                super(request);
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.SaveSettings] = SaveSettingsResponse;

        export interface DocumentEventSubscriptionResponseData {

            /** Clones of the received AsyncResult objects for the document events that were subscribed to. */
            Results: any[];
        }

        /**
         * Represents the response for subscribing to document events, where the response is sent from the app to the originating applet
         * after the applet has requested to be subscribe to document events.
         */
        export class DocumentEventSubscriptionResponse extends Response<Requests.DocumentEventSubscriptionRequest, DocumentEventSubscriptionResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.DocumentEventSubscriptionRequest) {
                super(request);
                this.Data = { Results: [] };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.DocumentEventSubscription] = DocumentEventSubscriptionResponse;

        export interface BindingEventSubscriptionResponseData {

            /** Clones of the received AsyncResult objects for the binding events that were subscribed to. */
            Results: any[];
        }

        /**
         * Represents the response for subscribing to binding events, where the response is sent from the app to the originating applet
         * after the applet has requested to be subscribe to the binding's events.
         */
        export class BindingEventSubscriptionResponse extends Response<Requests.BindingEventSubscriptionRequest, BindingEventSubscriptionResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.BindingEventSubscriptionRequest) {
                super(request);
                this.Data = { Results: [] };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.BindingEventSubscription] = BindingEventSubscriptionResponse;

        export interface BindingMethodResponseData {

            /** The flattened responses for the binding's methods that were called. */
            Results: any[];
        }

        /**
         * Represents the response from calling methods on a binding, where the response is sent from the app to the originating applet
         * after the applet has requested to call methods on a binding.
         */
        export class BindingMethodResponse extends Response<Requests.BindingMethodRequest, BindingMethodResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.BindingMethodRequest) {
                super(request);
                this.Data = { Results: [] };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.BindingMethod] = BindingMethodResponse;

        export interface DocumentMethodResponseData {

            /** The flattened responses for the binding's methods that were called. */
            Result?: any[];
        }

        /**
         * Represents the response from calling methods on the document, where the response is sent from the app to the originating applet
         * after the applet has requested to call a method on the document.
         */
        export class DocumentMethodResponse extends Response<Requests.DocumentMethodRequest, DocumentMethodResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.DocumentMethodRequest) {
                super(request);
                this.Data = {};
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.DocumentMethod] = DocumentMethodResponse;

        export interface BindingsMethodResponseData {

            /** The flattened response for the document bindings method that was called. */
            Result?: any[];
        }

        /**
         * Represents the response from calling methods on the document bindings, where the response is sent from the app to the originating applet
         * after the applet has requested to call a method on the document bindings.
         */
        export class BindingsMethodResponse extends Response<Requests.BindingsMethodRequest, BindingsMethodResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.BindingsMethodRequest) {
                super(request);
                this.Data = {};
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.BindingsMethod] = BindingsMethodResponse;

        export interface OfficeActionResponseData {

            /** The requested return values of the actions performed in the form of a property bag keyed by variable name. */
            Values: util.PropertyBag<any>;

            /** The AsyncResult objects received from the methods executed in the Office scope. */
            Results: any[];
        }

        /** Represents the response from Office action execution, where the response is sent from the app to the originating applet. */
        export class OfficeActionResponse extends Response<Requests.OfficeActionRequest, OfficeActionResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.OfficeActionRequest) {
                super(request);
                this.Data = { Values: {}, Results: [] };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.OfficeAction] = OfficeActionResponse;

        export interface PromptUserResponseData {

            /** The user's response to the question they were prompted. */
            UserResponse: boolean;
        }

        /**
         * Represents the response from prompting the user for a boolean value, where the response is sent from the app to the originating applet
         * after the applet has requested to prompt the user.
         */
        export class PromptUserResponse extends Response<Requests.PromptUserRequest, PromptUserResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param userResponse The user's response to the question they were prompted.
             */
            constructor(request: Requests.PromptUserRequest, userResponse: boolean) {
                super(request);
                this.Data = { UserResponse: userResponse };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.PromptUser] = PromptUserResponse;

        export interface ResourceResponseData {

            /** The response object sent from a resource to send back in reply to an applet. */
            ResourceResponse: any;
        }

        export interface ResourceResponseOptions {

            /** The response object sent from a resource to send back in reply to an applet. */
            ResourceResponse?: any;
        }

        /**
         * Represents the response from interacting with a resource, where the response is sent from the app to the applet that
         * originally requested to interact with a resource.
         */
        export class ResourceResponse extends Response<Requests.ResourceRequest, ResourceResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param options The options to include in the response.
             */
            constructor(request: Requests.ResourceRequest, options: ResourceResponseOptions = {}) {
                super(request);
                this.Data = { ResourceResponse: options.ResourceResponse };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.Resource] = ResourceResponse;

        export interface InterAppletResponseData {

            /** The response object sent from the applet. */
            AppletResponse: any;
        }

        /**
         * Represents the response from another applet in the app, where the response is sent from the app to the applet that
         * sent the original applet request.
         */
        export class InterAppletResponse extends Response<Requests.InterAppletRequest, InterAppletResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param appletResponse The response object sent from the source applet in reply to the originating applet.
             */
            constructor(request: Requests.InterAppletRequest, appletResponse: any) {
                super(request);
                this.Data = { AppletResponse: appletResponse };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.InterApplet] = InterAppletResponse;

        export interface ChangeConnectionAddressResponseData {

            /** Indicates if the add-in will reconnect to the new connection address. */
            WillReconnect: boolean;
        }

        /** Represents the response from the app, indicating if the add-in will reload and reconnect to the previously provided connection address. */
        export class ChangeConnectionAddressResponse extends Response<Requests.ChangeConnectionAddressRequest, ChangeConnectionAddressResponseData> {

            /**
             * @param request The request message that correlates to the response.
             * @param willReconnect Indicates if the add-in will reconnect to the new connection address.
             */
            constructor(request: Requests.ChangeConnectionAddressRequest, willReconnect: boolean) {
                super(request);
                this.Data = { WillReconnect: willReconnect };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.ChangeConnectionAddress] = ChangeConnectionAddressResponse;

        export interface GetFocusResponseData { }

        /**
         * Represents the response for subscribing to focus events, where focus will be placed inside the applet iframe.
         */
        export class GetFocusResponse extends Response<Requests.GetFocusRequest, GetFocusResponseData> {

            /**
             * @param request The request message that correlates to the response.
             */
            constructor(request: Requests.GetFocusRequest) {
                super(request);
                this.Data = { };
            }
        }

        appletMessaging.expectedResponseTypes[appletMessaging.MessageTypes.FocusInIFame] = GetFocusResponse;
    }

    export type ResponseType = Responses.Response<Requests.Request<any>, any>;

    /** Represents an updated option that should be updated within the app. */
    export class UpdatedOption {

        /** The index of the group containing the option value to update. */
        public GroupIndex: number;

        /** The index of the option, within the group, containing the value to update. */
        public OptionIndex: number;

        /** The updated option value. */
        public Value: any;

        /**
         * @param groupIndex The index of the group containing the option value to update.
         * @param optionIndex The index of the option, within the group, containing the value to update.
         * @param value The updated option value.
         */
        constructor(groupIndex: number, optionIndex: number, value: any) {
            this.GroupIndex = groupIndex;
            this.OptionIndex = optionIndex;
            this.Value = value;
        }
    }

    /* jshint -W069 */
    /** The sources where an event can originate. */
    export enum OfficeEventSource {

        /** The document as the source of the event. */
        Document = 0,

        /** The binding as the source of the event. */
        Binding = 1
    }
    /* jshint +W069 */

    export interface OfficeActionOptions {

        /** The scope at which to start resolving the reference path; defaults to context. */
        Scope?: OfficeActionScope;

        /** The name to store the result of the action under in the local scope. */
        VariableName?: string;

        /** Indicates whether the result of the action should be returned in the message response under the provided variable name. */
        ReturnResult?: boolean;

        /** The value to assign to the reference at the resolved path; only applies if VariableName is not supplied. */
        AssignmentValue?: any;

        /** Indicates whether the context should be synced with Office after executing the action. */
        Sync?: boolean;
    }

    /** Represents an action to perform using the Office API. */
    export class OfficeAction {

        /** The path of references to resolve, in order, to perform the action. */
        public Path: OfficeReference[];

        /** The scope at which to start resolving the reference path. */
        public Scope: OfficeActionScope;

        /** The name to store the result of the action under in the local scope. */
        public VariableName: string;

        /** Indicates whether the result of the action should be returned in the message response under the provided variable name. */
        public ReturnResult: boolean;

        /** The value to assign to the reference at the resolved path. */
        public AssignmentValue: any;

        /** Indicates whether the context should be synced with Office after executing the action. */
        public Sync: boolean;

        /**
         * @param path An array of references to resolve, in order as a path, to perform the action.
         * @param options Optional parameters with which to initialize the reference.
         */
        constructor(
            path: OfficeReference[],
            options: OfficeActionOptions = {}) {

            this.Path = path;
            this.Scope = options.Scope || OfficeActionScope.Context;
            this.VariableName = options.VariableName;
            this.ReturnResult = options.ReturnResult;
            this.Sync = options.Sync;

            if ("AssignmentValue" in options) {
                this.AssignmentValue = options.AssignmentValue;
            }
        }
    }

    export interface OfficeReferenceOptions {

        /** The arguments to send to the method if the object is a function. */
        Arguments?: any[];

        /** Indicates whether the provided arguments should be iterated over to resolve the reference multiple times. */
        ExecuteAsLoop?: boolean;
    }

    /** Represents a reference to an Office object. */
    export class OfficeReference {

        /** The name of the referenced object. */
        public Name: string;

        /** The type of the referenced object. */
        public Type: OfficeReferenceType;

        /** The arguments to send to the method if the object is a function. */
        public Arguments: any[];

        /** Indicates whether the provided arguments should be iterated over to resolve the reference multiple times. */
        public ExecuteAsLoop: boolean;

        /**
         * @param name The name of the referenced object.
         * @param referenceType The type of the referenced object.
         * @param options Optional parameters with which to initialize the reference.
         */
        constructor(name: string, referenceType: OfficeReferenceType, options: OfficeReferenceOptions = {}) {
            this.Name = name;
            this.Type = referenceType;
            this.Arguments = options.Arguments;
            this.ExecuteAsLoop = options.ExecuteAsLoop;
        }
    }

    /* jshint -W069 */
    /** Represents the scopes at which an office action can start pathing. */
    export enum OfficeActionScope {

        /** The scope of the current request context. */
        Context = 0,

        /** The local scope; depends on previously defined variables. */
        Local = 1,

        /** The global Office scope. */
        Office = 2,

        /** The scope of the result object received from the previous asynchronous operation. */
        AsyncResult = 3
    }

    /** Represents how Office references can be resolved. */
    export enum OfficeReferenceType {

        /** Resolves the Office reference as a property. */
        Property = 0,

        /** Resolves the Office reference as a function. */
        Function = 1
    }
    /* jshint +W069 */

    export interface OfficeMethodInformationOptions {

        /** The arguments to send to the method. */
        Arguments?: any[];

        /** The options to send to the method. */
        Options?: any;

        /** Indicates if the method is required; true by default. */
        IsRequired?: boolean;
    }

    /** Represents the information about calling a method on an Office object. */
    export class OfficeMethodInformation {

        /** The name of the method to call on the Office object. */
        public MethodName: string;

        /** The arguments to send to the method. */
        public Arguments: any[];

        /** The options to send to the method. */
        public Options: any;

        /** Indicates if the method is required; if it is then a failed result will be returned if the method isn't found. */
        public IsRequired: boolean;

        /**
         * @param methodName The name of the method to call on the Office object.
         * @param options The options to include in the method information.
         */
        constructor(methodName: string, options: OfficeMethodInformationOptions = {}) {
            this.MethodName = methodName;
            this.Arguments = options.Arguments;
            this.Options = options.Options;
            this.IsRequired = options.IsRequired === undefined ? true : options.IsRequired;
        }
    }

    /** The well known names of methods that can be performed. */
    export namespace MethodNames {

        /** The well known names of document methods that can be performed. */
        export namespace Document {

            /** The name of the document set selected data method. */
            export const SetSelectedData = "setSelectedDataAsync";

            /** The name of the document get selected data method. */
            export const GetSelectedData = "getSelectedDataAsync";

            /** The name of the document go to by ID method. */
            export const GoToByID = "goToByIdAsync";
        }

        /** The well known names of document bindings methods that can be performed. */
        export namespace Bindings {

            /** The name of the document bindings add from selection method. */
            export const AddFromSelection = "addFromSelectionAsync";

            /** The name of the document bindings add from named item method. */
            export const AddFromNamedItem = "addFromNamedItemAsync";

            /** The name of the document bindings release binding method. */
            export const ReleaseBinding = "releaseByIdAsync";
        }

        /** The well known names of binding methods that can be performed. */
        export namespace Binding {

            /** The name of the binding get data method. */
            export const GetData = "getDataAsync";

            /** The name of the binding set data method. */
            export const SetData = "setDataAsync";

            /** The well known names of table binding methods that can be performed. */
            export namespace TableBinding {

                /** The name of the table binding add rows method. */
                export const AddRows = "addRowsAsync";

                /** The name of the table binding delete all data method. */
                export const DeleteAllData = "deleteAllDataValuesAsync";

                /** The name of the table binding set formats method. */
                export const SetFormats = "setFormatsAsync";

                /** The name of the table binding clear formats method. */
                export const ClearFormats = "clearFormatsAsync";

                /** The name of the table binding add columns method. */
                export const AddColumns = "addColumnsAsync";
            }
        }
    }

    /** Represents the data in a table or table binding. */
    export class OfficeTableData {

        /** The headers in the table. */
        public headers: string[] | string[][];

        /** The rows in the table. */
        public rows: any[][];
    }

    /** Represents a group of options for an applet that should be rendered in the options dialog within the app. */
    export class OptionsGroup {

        /** The label to show for the option group heading. */
        public Label: string;

        /** The options available within the options group. */
        public Options: OptionInformation[];

        /**
         * @param label The label to show for the option group heading.
         */
        constructor(label: string) {
            this.Label = label;
            this.Options = [];
        }
    }

    /** Represents an option for an applet that should be rendered in the options dialog within the app. */
    export class OptionInformation {

        /** The type of the option. */
        public Type: OptionInformationType;

        /** The label to show for the option. */
        public Label: string;

        /** The value of the option; the type of the value will differ based on the type of option. */
        public Value: any;

        // tslint:disable:no-reserved-keywords
        /**
         * @param type The type of the option.
         * @param label The label to show for the option.
         * @param value The value of the option; the type of the value will differ based on the type of option.
         */
        constructor(type: OptionInformationType, label: string, value: any) {
            this.Type = type;
            this.Label = label;
            this.Value = value;
        }
        // tslint:enable:no-reserved-keywords
    }

    /* jshint -W069 */
    /** The types of options supported by the app. */
    export enum OptionInformationType {

        /** The type indicating a string option, where the value should be a string value. */
        String = 0,

        /** The type indicating a boolean option, where the value should be true or false value. */
        Boolean = 1,

        /** The type indicating an action option, where the value should be the name of an action to perform by the applet. */
        Action = 2,

        /** The type indicating an option that doesn't change, for display purposes only. */
        DisplayOnly = 3
    }

    /** The well known actions the app can perform. */
    export enum AppActions {

        /** Opens the message center. */
        OpenMessageCenter = 0,

        /** Opens the add-in settings page. */
        OpenSettings = 1,
    }

    /** The types of notifications. */
    export enum NotificationType {

        /** The normal notification type. */
        notification = "notification",

        /** The error notification type. */
        error = "error"
    }
    /* jshint +W069 */

    export interface NotificationOptions {

        /** The detailed error message. */
        Details?: string;

        /** Indicates whether a time stamp should be displayed with the notification; true by default. */
        IncludeTimeStamp?: boolean;

        /** Indicates if the correlation ID should be displayed with the notification; false by default. This only applies to displayed errors. */
        IncludeCorrelationID?: boolean;

        /** Includes telemetry properties that should be logged. This only applies to displayed errors. */
        TelemetryProperties?: util.PropertyBag<any>;

        /** Contains 'Text' and 'Action' or 'AppAction' string properties that indicate how the option link should be displayed. */
        LinkOptions?: ILinkOptions;

        /** Indicates if the notification should be displayed in an popup box to the user; false by default for notifications. */
        DisplayToUser?: boolean;

        /** The corresponding error code, used for error notifications. */
        ErrorCode?: string;
    }

    /** Represents a basic notification. */
    export class Notification {

        /** The notification type. */
        public Type: appletMessaging.NotificationType;

        /** The notification message. */
        public Message: string;

        /** The detailed notification message. */
        public Details: string;

        /** Indicates whether a time stamp should be displayed with the notification. */
        public IncludeTimeStamp: boolean;

        /** Indicates if the correlation ID should be displayed with the notification; this only applies to displayed errors. */
        public IncludeCorrelationID: boolean;

        /** An object containing 'Text' and 'Action' or 'AppAction' string properties that indicate how the option link should be displayed. */
        public LinkOptions: ILinkOptions;

        /** Includes telemetry properties that should be logged. This only applies to displayed errors. */
        public TelemetryProperties: util.PropertyBag<any>;

        /** Indicates if the notification should be displayed in a popup box to the user. */
        public DisplayToUser: boolean;

        /** The corresponding error code, used for error notifications. */
        public ErrorCode: string;

        /**
         * @param notificationType The notification type.
         * @param message The notification message.
         * @param options The notification options to display.
         */
        constructor(notificationType: appletMessaging.NotificationType, message: string, options: NotificationOptions = {}) {
            this.Type = notificationType;
            this.Message = message;
            this.Details = options.Details;
            this.IncludeTimeStamp = options.IncludeTimeStamp === undefined ? true : options.IncludeTimeStamp;
            this.IncludeCorrelationID = options.IncludeCorrelationID;
            this.LinkOptions = options.LinkOptions;
            this.DisplayToUser = options.DisplayToUser;
            this.TelemetryProperties = options.TelemetryProperties;
            this.ErrorCode = options.ErrorCode;
        }
    }

    /**
     * The format used to generate a unique message ID where the first format parameter is the sender ID, the second is the message
     * type, and the third is a number that is unique to the sender.
     */
    const messageIdFormat = "msg-{0}-{1}-{2}";

    /** The last message number, used to ensure messages are unique. */
    export let _lastMessageNumber = 0;

    /**
     * Creates a unique message ID based on the provided sender ID and message type.
     * @param id The unique ID of the sender.
     * @param messageType The type of message to generate the unique message ID for.
     * @returns A unique message ID based on the provided sender ID and message type.
     */
    export function createUniqueMessageID(id: string, messageType: string): string {
        return $format(messageIdFormat, id, messageType, ++_lastMessageNumber);
    }

    export interface CreateCloneableDeepCopyOptions {

        /** The array of objects that have already been copied; this is used to handle cyclical object references. */
        HandledObjectsArray?: any[];

        /**
         * The array of object copies that is indexed the same as the HandledObjectsArray containing the object copies corresponding
         * to the copied object at the same index within HandledObjectsArray
         */
        ObjectCopiesArray?: any[];
    }

    /**
     * Creates a deep copy of the provided object that is cloneable, whereby it doesn't copy non-cloneable members like functions. This enables passing
     * near copies of objects between frames, where the information about the object is copied, but its functionality is not.
     * @param obj The object to copy.
     * @param callback The callback to perform once the provided object is copied. The callback will be sent a new cloneable deep copy of the provided object as its only parameter.
     * @param options The options used to generate a cloneable deep copy of the provided object.
     */
    export function createCloneableDeepCopy(obj: any, callback: (copy?: any) => void, options: CreateCloneableDeepCopyOptions = {}): void {

        switch (typeof obj) {
            case "function":
                callback();
                // Skip objects that are functions
                break;
            case "object":

                // If the object is not a primitive, ensure the optional object arrays are initialized
                if (options.HandledObjectsArray === undefined || options.ObjectCopiesArray === undefined) {
                    options.HandledObjectsArray = [];
                    options.ObjectCopiesArray = [];
                }

                if ($.isArray(obj)) {
                    new _AsyncArrayCopier(obj, callback, options).copy();
                } else if (obj === null || obj instanceof Date || obj instanceof RegExp || ("Office" in window && Office !== undefined && Office.TableData !== undefined && obj instanceof Office.TableData)) {
                    // If the object is an already cloneable type, don't try to copy it, just treat it like a primitive and set it as the return value
                    callback(obj);
                } else {

                    // Otherwise the object is a "normal" object and should be copied member by member
                    var handledObjectIndex = options.HandledObjectsArray.indexOf(obj);
                    if (handledObjectIndex === -1) {
                        new _AsyncObjectCopier(obj, callback, options).copy();
                    } else {

                        // If the object was already handled, just set the return value as the handled object's copy
                        callback(options.ObjectCopiesArray[handledObjectIndex]);
                    }
                }
                break;
            default:
                // Anything else, assume that it can be copied (not always the case, but can ammend this logic as needed in the future)
                callback(obj);
        }
    }

    /** Creates a deep copy of the provided array that is cloneable. */
    class _AsyncArrayCopier {

        private _originalArray: any[];

        /** The copy of the provided array. */
        private _arrayCopy: any[];

        /** The length of the provided array. */
        private _arrayLength: number;
        private _callback: (cloneableArray: any[]) => void;
        private _options: CreateCloneableDeepCopyOptions;

        /** The max number of elements to copy in a single batch before giving control back to the browser briefly and scheduling the next batch of elements to be copied. */
        private static _BatchSize = 128;

        /**
         * @param array The array to copy.
         * @param callback The callback to perform once the provided array is copied. The callback will be sent a new cloneable deep copy of the provided array as its only parameter.
         * @param options The options used to generate a cloneable deep copy of the provided array.
         */
        constructor(array: any[], callback: (cloneableArray: any[]) => void, options: CreateCloneableDeepCopyOptions = {}) {
            /* jshint validthis: true */
            this._originalArray = array;
            this._arrayCopy = [];
            this._arrayLength = array.length;
            this._callback = callback;
            this._options = options;
            this.copy = this.copy.bind(this);
            this._copyArrayItem = this._copyArrayItem.bind(this);
        }

        /** Begins the process of copying the provided array. */
        public copy(): void {
            this._copyArrayItem(0);
        }

        /**
         * Copies the element within the array at the provided index to the copy of the array. If the index is out of bounds
         * for the array, the provided callback is called as the copy is complete.
         * @param index The index of the element within the provided array to copy.
         */
        private _copyArrayItem(index: number): void {

            if (index >= this._arrayLength) {
                this._callback(this._arrayCopy);
                return;
            }

            appletMessaging.createCloneableDeepCopy(
                this._originalArray[index],
                (copiedElement: any) => {
                    this._arrayCopy.push(copiedElement);

                    if (index > 0 && index % _AsyncArrayCopier._BatchSize) {
                        window.setTimeout(() => { this._copyArrayItem(index + 1); }, 10);
                    } else {
                        this._copyArrayItem(index + 1);
                    }
                },
                {
                    HandledObjectsArray: this._options.HandledObjectsArray,
                    ObjectCopiesArray: this._options.ObjectCopiesArray
                });
        }
    }

    /** Creates a deep copy of the provided object that is cloneable. */
    class _AsyncObjectCopier {

        private _originalObject: any;

        /** The copy of the provided object. */
        private _objectCopy: any;

        /** The names of the properties the object contains. */
        private _propertyNames: string[];

        /** The number of properties the object contains. */
        private _propertyNamesLength: number;
        private _callback: (cloneableObject: any) => void;
        private _options: CreateCloneableDeepCopyOptions;

        /**
         * @param obj The object to copy.
         * @param callback The callback to perform once the provided object is copied. The callback will be sent a new cloneable deep copy of the provided object as its only parameter.
         * @param options The options used to generate a cloneable deep copy of the provided object.
         */
        constructor(obj: any, callback: (cloneableObject: any) => void, options: CreateCloneableDeepCopyOptions) {
            /* jshint validthis: true */
            this._originalObject = obj;
            this._objectCopy = {};
            this._propertyNames = Object.keys(obj);
            this._propertyNamesLength = this._propertyNames.length;
            this._callback = callback;
            this._options = options;
            this.copy = this.copy.bind(this);
            this._copyNextMember = this._copyNextMember.bind(this);
        }

        /** Begins the process of copying the provided object. */
        public copy() {
            this._copyNextMember(0);
        }

        /**
         * Copies the property within the object at the provided property names index to the copy of the object. If the index is out of bounds
         * for the property names array, the provided callback is called as the copy is complete.
         * @param index The index of the property name within the property names array representing the property to copy.
         */
        private _copyNextMember(index: number): void {

            if (index >= this._propertyNamesLength) {
                this._callback(this._objectCopy);
                return;
            }

            var propertyName = this._propertyNames[index];
            this._options.HandledObjectsArray.push(this._originalObject);
            this._options.ObjectCopiesArray.push(this._objectCopy);
            appletMessaging.createCloneableDeepCopy(
                this._originalObject[propertyName],
                (copiedObject: any) => {
                    this._objectCopy[propertyName] = copiedObject;
                    this._copyNextMember(index + 1);
                },
                {
                    HandledObjectsArray: this._options.HandledObjectsArray,
                    ObjectCopiesArray: this._options.ObjectCopiesArray
                });
        }
    }

    /**
     * Stamps the TelemetryDetails property with build information about the currently running applet.
     * @param options An object that contains a TelemetryDetails property that build information will be added to.
     */
    export function addBuildInfoToMessage(options?: { TelemetryDetails?: string | util.PropertyBag<any> }): void {

        var build = window.dynamicsOfficeBuild || {};
        var telemetryDetails: util.PropertyBag<any> = { AppletVersion: build.buildVersion, AppletBuildType: build.buildType };

        if (options.TelemetryDetails !== undefined) {
            if (options.TelemetryDetails instanceof String || typeof (options.TelemetryDetails) === "string") {
                // String details are converted to a "TelemetryDetails" property
                (telemetryDetails as any).TelemetryDetails = options.TelemetryDetails;
            } else if (options.TelemetryDetails instanceof Object || typeof (options.TelemetryDetails) === "object") {
                $.extend(true, telemetryDetails, options.TelemetryDetails);
            }
        }

        options.TelemetryDetails = telemetryDetails;
    }

    export interface MessageTypeHandler { (event: MessageEvent): void }

    export interface ResponseHandler { (response: any): void }

    /** Handles messages for a given applet, enabling registration of callbacks when a message is received. */
    export class MessageHandler {

        /** The message delegate used for the request timed out message. */
        private requestTimedOutMessage: () => string;

        /**
         * Callback to perform whenever an event is received that verifies if the message is valid for the message handler. The callback
         * takes the received message event as its only parameter, and must return either true if the message event can be handled
         * or false if the message event should be ignored.
         */
        private _messageVerificationCallback: (event: any) => boolean;

        /** The message handlers map that ties a type of message to a handler to call when the message is received. */
        private _messageHandlers: util.PropertyBag<MessageTypeHandler>;

        /** The response callbacks map that ties a specific response message, by its ID, to a callback to perform when the response message is received. */
        private _responseCallbacks: util.PropertyBag<ResponseHandler>;

        /**
         * The pending requests map that ties a request, by its ID, that is waiting for a response to its corresponding verification method timeout ID used to ensure a
         * response is received in a timely manner.
         */
        private _pendingRequests: util.PropertyBag<number>;

        /** The default number of milliseconds to wait before considering a request's response to have timed out. */
        private static _DefaultResponseTimeout = 20000;

        /**
         * @param requestTimedOutMessage The message delegate used for the request timed out message.
         * @param messageVerificationCallback Callback to perform whenever an event is received that verifies if the message is valid for the message handler. The callback
         *                                    takes the received message event as its only parameter, and must return either true if the message event can be handled
         *                                    or false if the message event should be ignored. If the parameter isn't provided, it is assumed that all messages should be considered.
         */
        constructor(requestTimedOutMessage: () => string, messageVerificationCallback?: (event: any) => boolean) {

            this.requestTimedOutMessage = requestTimedOutMessage;
            this._messageVerificationCallback = messageVerificationCallback;
            this._messageHandlers = {};
            this._responseCallbacks = {};
            this._pendingRequests = {};

            this.listen = this.listen.bind(this);
            this.sendMessage = this.sendMessage.bind(this);
        }

        /**
         * Listens for messages sent to this page and, upon receiving a message, calls any callbacks registered for a
         * specific response message or calls any registered message handlers if a callback hasn't been registered for
         * the specific message (or the message isn't a response).
         * @param event The message event received by this listener.
         */
        public listen(event: any): void {

            // If the message isn't supported for any reason, return early and do not process the message further
            if (this._messageVerificationCallback && !this._messageVerificationCallback(event)) {
                return;
            }

            var messageType;
            var responseMessageID;
            var isPartialResponse;

            // If the message is in the required format, retrieve the type and response message ID of the message
            if (event.data !== undefined) {

                // If the message is a response message containing a request object, retrieve the type and ID of the message
                if (event.data.Request !== undefined) {
                    messageType = event.data.Request.Type;
                    responseMessageID = event.data.Request.MessageID;
                    isPartialResponse = event.data.Partial === true;

                    // For resource responses, extract the Partial property from the ResourceResponse object
                    if (!isPartialResponse && event.data.Data !== undefined && event.data.Data.ResourceResponse !== undefined) {
                        isPartialResponse = event.data.Data.ResourceResponse.Partial === true;
                    }
                }

                // If the message wasn't a response containing a request object, try retrieving the type of the message
                // from the message itself (e.g. the message is a request message)
                if (responseMessageID === undefined && messageType === undefined) {
                    messageType = event.data.Type;
                }
            }

            var messageCallback: ResponseHandler;

            // If the response message ID of the message was able to be retrieved, retrieve the message's registered callback
            if (responseMessageID !== undefined) {
                messageCallback = this._responseCallbacks[responseMessageID];

                // Clear the timeout and remove the pending request if more responses are not expected
                if ((responseMessageID in this._pendingRequests) && !isPartialResponse) {
                    window.clearTimeout(this._pendingRequests[responseMessageID]);
                    delete this._pendingRequests[responseMessageID];
                }
            }

            // If there was a callback registered for the response message, call the callback;
            // otherwise see if there was a handler registered that should be used instead
            if (messageCallback) {
                messageCallback(event.data);

                // Remove the callback if more responses are not expected
                if (!isPartialResponse) {
                    delete this._responseCallbacks[responseMessageID];
                }
            } else {
                var messageHandler: MessageTypeHandler;

                // If the type of the message was able to be retrieved, retrieve the handler for those types of messages
                if (messageType !== undefined) {
                    messageHandler = this._messageHandlers[messageType];
                }

                // If there was a handler registered for the type of message, call the handler
                if (messageHandler) {
                    messageHandler(event);
                }
            }
        }

        /**
         * Sends the message to the provided source.
         * @param source The source to which the message should be sent.
         * @param origin The origin of the source to which the message should be sent.
         * @param message The message to send.
         * @param options The options that determine how the message should be sent.
         */
        public sendMessage(
            source: Window,
            origin: string,
            message: any,
            options: {

                /** Indicates the amount of time in milliseconds to wait for a response before the request is considered timed out and a failed response is sent. */
                ResponseTimeout?: number
            } = {}): void {

            var ResponseType;

            if (message.ExpectedResponseType !== undefined) {
                ResponseType = message.ExpectedResponseType;
                delete message.ExpectedResponseType;
                var responseTimeout = options.ResponseTimeout === undefined ? appletMessaging.MessageHandler._DefaultResponseTimeout : options.ResponseTimeout;

                if (responseTimeout !== 0) {
                    this._pendingRequests[message.MessageID] =
                        window.setTimeout(() => {
                            if (message.MessageID in this._pendingRequests) {
                                delete this._pendingRequests[message.MessageID];
                                var response = new ResponseType(message);
                                response.Status = appletMessaging.ResponseStatus.failure;
                                response.FailureMessage = this.requestTimedOutMessage();
                                appletMessaging.createCloneableDeepCopy(response, (responseCopy) => {
                                    this._sendMessageToWindow(responseCopy);
                                });
                            }
                        }, responseTimeout);
                }
            }

            source.postMessage(message, origin);

            if (message.IncludeDataInResponse === false) {
                delete message.Data;
            }

            message.ExpectedResponseType = ResponseType;
        }

        /**
         * Sends the provided message to the current window.
         * @param message The message to send.
         */
        private _sendMessageToWindow(message: any): void {
            try {
                window.postMessage(message, window.location.origin);
            } catch (e) {
                appletMessaging.createCloneableDeepCopy(message, function messageCopied(messageCopy) {
                    window.postMessage(messageCopy, window.location.origin);
                });
            }
        }

        /**
         * Registers a handler to perform when messages of a specific type are received.
         * @param messageType The message type that the handler is being registered for.
         * @param handler The handler to execute when a message with the provided message type is received. The function will be sent
         *                the received event containing the message.
         */
        public registerMessageHandler(messageType: string, handler: MessageTypeHandler): void {
            this._messageHandlers[messageType] = handler;
        }

        /**
         * Gets the message handler associated with the provided message type.
         * @param messageType The message type for which to retrieve the handler.
         * @returns The message handler associated with the provided message type.
         */
        public getMessageHandler(messageType: string): MessageTypeHandler {
            return this._messageHandlers[messageType];
        }

        /**
         * Unregisters a handler associated with the provided message type.
         * @param messageType The message type that the handler is being unregistered for.
         */
        public unregisterMessageHandler(messageType: string): void {
            delete this._messageHandlers[messageType];
        }

        /**
         * Determines if a message handler has been registered for the provided message type.
         * @param messageType The message type for which to determine if a handler is registered.
         * @returns A value indicating if a message handler is registered for the provided message type.
         */
        public isMessageTypeHandled(messageType: string): boolean {
            return messageType in this._messageHandlers;
        }

        /**
         * Registers a callback to perform for a specific response message that is received, based on its message ID.
         * @param messageID The ID of the message that the callback is being registered for.
         * @param callback The callback to execute when a message with the provided message ID is received. The function will be sent a single
         *                 response argument representing the response that was received.
         */
        public registerResponseCallback(messageID: string, callback: ResponseHandler): void {
            this._responseCallbacks[messageID] = callback;
        }

        /**
         * Gets a response callback associated with the provided message ID.
         * @param messageID The ID of the message for which the response callback is retrieved.
         * @returns The associated response callback.
         */
        public getResponseCallback(messageID: string): ResponseHandler {
            return this._responseCallbacks[messageID];
        }

        /**
         * Unregisters a response callback associated with the provided message ID.
         * @param messageID The message ID that the response callback is being unregistered for.
         */
        public unRegisterResponseCallback(messageID: string): void {
            delete this._responseCallbacks[messageID];
        }

        /**
         * Determines if a message response callback has been registered for the provided message ID.
         * @param messageID The ID of the message for which to determine if a callback is registered.
         * @returns A value indicating if a message response callback is registered for the provided message ID.
         */
        public isMessageResponseHandled(messageID: string): boolean {
            return messageID in this._responseCallbacks;
        }
    }
}
